<script setup lang="ts">
// icons
import { faHeart as fasHeart } from '@fortawesome/pro-solid-svg-icons';
import { faHeart } from '@fortawesome/pro-regular-svg-icons';

import CiAwesomeIcon from '@/components/common/CiAwesomeIcon/CiAwesomeIcon.vue';

export interface CiCampsiteFavoriteProps {
  active: boolean;
  iconRatio?: string;
  isCpdp?: boolean;
  showLabel?: boolean;
}
const props = withDefaults(defineProps<CiCampsiteFavoriteProps>(), {
  iconRatio: '1',
  isCpdp: false,
  showLabel: true,
});

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const iconRef = useTemplateRef('iconRef');
const iconClasses = ref(['fill-danger', props.isCpdp ? 'mb-2 md:mb-0 md:mr-4' : '']);
const model = ref(props.active);

// Memoize the icon computation
const favIcon = computed(() => (model.value ? fasHeart : faHeart));

// Track animation state
const isAnimating = ref(false);

/********************
 * WATCHERS          *
 ********************/
watch(() => props.active, onActiveChange, { immediate: false });

/********************
 * METHODS           *
 ********************/
function onActiveChange(newValue: boolean) {
  model.value = newValue;
  isAnimating.value = true;

  // Use CSS classes for animation instead of setTimeout
  iconRef.value?.$el?.classList?.add(newValue ? 'animate-[heartbeat_1s_ease]' : 'animate-[fade-out_1s_ease]');

  // Use requestAnimationFrame for better performance
  requestAnimationFrame(() => {
    setTimeout(() => {
      iconRef.value?.$el?.classList?.remove('animate-[heartbeat_1s_ease]');
      iconRef.value?.$el?.classList?.remove('animate-[fade-out_1s_ease]');
      isAnimating.value = false;
    }, 1000);
  });
}
</script>

<template>
  <div
    class="fav-button group cursor-pointer text-center"
    data-testid="ci-campsite-favorite"
    :data-favorite-status="`${props.active}`"
  >
    <div
      class="flex items-center justify-center font-medium"
      :class="{ 'text-tiny-book flex-col text-dark md:flex-row': props.isCpdp }"
    >
      <ClientOnly>
        <CiAwesomeIcon
          ref="iconRef"
          :icon="favIcon"
          class="fav-icon transition-transform duration-200 ease-in-out group-hover:scale-125"
          :class="[iconClasses]"
          :ratio="props.iconRatio"
        />
        <span
          v-if="props.showLabel"
          key="active"
          class="uppercase"
          :data-testid="props.active ? 'ci-campsite-favorite--active' : 'ci-campsite-favorite--inactive'"
        >
          <span>{{ $gettext('Favorite') }}</span>
        </span>
        <template #fallback>
          <CiAwesomeIcon
            ref="iconRef"
            :icon="faHeart"
            class="fav-icon"
            :class="[iconClasses]"
            :ratio="props.iconRatio"
          />
        </template>
      </ClientOnly>
    </div>
  </div>
</template>
